import { useEffect, useRef, useState } from 'react';
import { Annotation, EditorState, StateEffect, type Extension } from '@codemirror/state';
import { EditorView, ViewUpdate, keymap, placeholder } from '@codemirror/view';
import { closeBrackets } from '@codemirror/autocomplete';
import { syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { standardKeymap, history, historyKeymap } from '@codemirror/commands';
import { tags } from '@lezer/highlight';

import { getTheme } from '@/lib/theme';
import { getThemeColors } from '@/lib/theme-colors';

export const External = Annotation.define<boolean>();

export interface UseCodemirrorProps {
  value: string;
  onChange?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onInitialize?: (view: EditorView) => void;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  extensions?: Extension[];
}

function getSyntaxHighlightStyle() {
  const c = getThemeColors();
  return HighlightStyle.define([
    { tag: tags.bool, color: c.blue60 },
    { tag: tags.comment, color: c.neutral50 },
    { tag: tags.keyword, color: c.teal50 },
    { tag: tags.null, color: c.purple60 },
    { tag: tags.number, color: c.purple50 },
    { tag: tags.bracket, color: c.neutral100 },
    { tag: tags.punctuation, color: c.neutral100 },
    { tag: tags.string, color: c.orange50 },
  ]);
}

const supCodemirrorStyle = getSyntaxHighlightStyle();

export function useCodeMirror(props: UseCodemirrorProps & { container: HTMLDivElement | null }) {
  const { value, onChange, autoFocus } = props;
  const [container, setContainer] = useState<HTMLDivElement | null>();
  const [view, setView] = useState<EditorView>();
  const [state, setState] = useState<EditorState>();
  // Doc values emitted via onChange that have not yet come back around as the
  // `value` prop. Used to tell externally-changed values apart from echoes of
  // the editor's own output.
  const emittedValues = useRef<string[]>([]);

  const extensions = [
    EditorView.updateListener.of((update) => {
      if (update.docChanged && !isExternalUpdate(update) && onChange !== undefined) {
        const doc = update.state.doc.toString();
        emittedValues.current.push(doc);
        onChange(doc);
      }
    }),
    // Base config for all SUP editors
    EditorState.tabSize.of(2),
    closeBrackets(),
    history(),
    keymap.of(standardKeymap),
    keymap.of(historyKeymap),
    EditorView.domEventHandlers({ keydown: props.onKeyDown }),
    // Do not bubble up undo events
    keymap.of([{ key: 'Mod-z', run: () => true, preventDefault: true, stopPropagation: true }]),
    EditorView.lineWrapping,
    placeholder(props.placeholder ?? ''),
    // Base styling for all SUP editors
    EditorView.theme({}, { dark: getTheme() === 'dark' }),
    syntaxHighlighting(supCodemirrorStyle),
    props.extensions ?? [],
  ];

  useEffect(() => {
    if (container && !state) {
      const stateCurrent = EditorState.create({ doc: value, extensions });
      setState(stateCurrent);
      if (!view) {
        const viewCurrent = new EditorView({ state: stateCurrent, parent: container });
        setView(viewCurrent);

        if (props.onInitialize !== undefined) {
          props.onInitialize(viewCurrent);
        }
      }
    }

    return () => {
      if (view) {
        setState(undefined);
        setView(undefined);
      }
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, state]);

  useEffect(() => setContainer(props.container), [props.container]);

  useEffect(
    () => () => {
      if (view) {
        view.destroy();
        setView(undefined);
      }
    },
    [view],
  );

  useEffect(() => {
    if (autoFocus === true && view !== undefined) {
      view.focus();
    }
  }, [autoFocus, view]);

  useEffect(() => {
    if (view === undefined) {
      return;
    }

    // Defer the reconfigure so it never runs while CodeMirror is already
    // applying another transaction; otherwise codemirror throws
    // "Calls to EditorView.update are not allowed while an update is in progress".
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) {
        return;
      }
      view.dispatch({ effects: StateEffect.reconfigure.of(extensions) });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.extensions, onChange]);

  useEffect(() => {
    if (view === undefined) {
      return;
    }
    const currentValue = view.state.doc.toString();
    if (value === currentValue) {
      emittedValues.current = [];
      return;
    }
    // A `value` matching an earlier onChange emission is a stale echo of the
    // editor's own output — the doc has moved on since. Dispatching it would
    // overwrite what the user typed in the meantime, so drop it and wait for
    // the up-to-date value to come back around.
    const echoIndex = emittedValues.current.indexOf(value);
    if (echoIndex !== -1) {
      emittedValues.current = emittedValues.current.slice(echoIndex + 1);
      return;
    }
    emittedValues.current = [];
    view.dispatch({
      changes: { from: 0, to: currentValue.length, insert: value || '' },
      annotations: [External.of(true)],
    });
  }, [value, view]);

  return { state, setState, view, setView, container, setContainer };
}

const isExternalUpdate = (update: ViewUpdate) =>
  update.transactions.some((tr) => tr.annotation(External) ?? false);
